import { IAppContext } from 'App/AppContext';
import { DocumentOutputLocationSummary, DocumentOutputPurpose } from 'models/documentOutputLocation';
import ResourceList from 'models/resourceList';
import { apiGetOutputLocationSummary } from 'services/Api/documentOutputLocationService';
import { apiRequest } from 'services/Auth/authConfig';
import GlobalDataCache from './globalDataCache';

/**
 * The category and start folder of every output purpose, for every user: the screens outside the Reports tab read
 * here whether a purpose is bound and to which category. Loaded with the categories; the Reports tab reloads both
 * after a change.
 */
export class OutputLocationCache {
  appContext?: IAppContext;

  cacheRef?: GlobalDataCache;

  private locations: DocumentOutputLocationSummary[] | undefined;

  private isLoading: boolean = false;

  private locationsPromise: Promise<DocumentOutputLocationSummary[]> | undefined;

  async getItems(refresh: boolean = false): Promise<DocumentOutputLocationSummary[]> {
    if (this.isLoading && this.locationsPromise) {
      return await this.locationsPromise;
    }

    if (!this.locations || refresh) {
      if (this.appContext && !this.isLoading) {
        try {
          this.isLoading = true;
          const accessToken = await this.appContext.getAccessToken(apiRequest.scopes);
          this.locationsPromise = apiGetOutputLocationSummary(accessToken);
          this.locations = await this.locationsPromise;
        } catch (err) {
          this.appContext.setError(err);
        } finally {
          this.locationsPromise = undefined;
          this.isLoading = false;
        }
      }
    }

    return this.locations ?? [];
  }

  /** Drops the loaded locations, so the next getItems reads them again. */
  invalidate(): void {
    this.locations = undefined;
  }

  get(purpose: DocumentOutputPurpose): DocumentOutputLocationSummary | undefined {
    return this.locations?.find((location) => location.purpose === purpose);
  }

  /** The category the purpose is bound to; undefined when it is unbound or the category is not in the cache. */
  getList(purpose: DocumentOutputPurpose): ResourceList | undefined {
    const listId = this.get(purpose)?.listId;
    const lists = this.cacheRef?.lists;
    if (!listId || !lists?.has(listId)) return undefined;

    return lists.get(listId);
  }

  /** The purposes bound to the category, in purpose order. */
  getPurposesForList(listId: number | undefined): DocumentOutputPurpose[] {
    if (!listId || !this.locations) return [];

    return this.locations
      .filter((location) => location.listId === listId)
      .map((location) => location.purpose)
      .sort((a, b) => a - b);
  }

  clone(): OutputLocationCache {
    const cache = new OutputLocationCache();
    cache.locations = this.locations?.map((location) => location.clone());

    return cache;
  }
}
